/**
 * levelgen/rng.ts — The one source of randomness in level generation.
 *
 * `Math.random()` is deliberately not used anywhere in `levelgen/`. A generated
 * level has to be **reproducible from its seed**: a player sharing "seed
 * `market-day`, hard" should get the same town, a bug report has to be
 * re-openable, and a test that generates a hundred levels is worthless if it
 * generates different ones each run.
 *
 * ## Why streams are forked rather than shared
 *
 * Every stage of generation takes its own stream, forked from the level seed by
 * name (`rng.fork('trees')`). Sharing one stream would couple every stage to
 * every other: adding a single extra roll while placing the parks would shift
 * the whole sequence and silently rebuild every town in every test. Forking by
 * name means a change to the tree code changes the trees and nothing else,
 * which is what makes the generator safe to keep working on.
 */

/** A deterministic random stream. */
export interface Rng {
  /** The next value in [0, 1). */
  next(): number;
  /** A float in [min, max). */
  float(min: number, max: number): number;
  /** An integer in [min, max] — **inclusive** at both ends. */
  int(min: number, max: number): number;
  /** True with probability `p`. */
  chance(p: number): boolean;
  /** One item, uniformly. Throws on an empty list rather than returning undefined. */
  pick<T>(items: readonly T[]): T;
  /** A shuffled copy (Fisher–Yates). The input is left alone. */
  shuffle<T>(items: readonly T[]): T[];
  /**
   * An independent stream, derived from this one's seed and `label`. The same
   * label always gives the same substream, so stages stay decoupled.
   */
  fork(label: string): Rng;
}

/**
 * FNV-1a over the seed string, so a human-typed seed ("market-day") maps to a
 * well-mixed 32-bit number. Numeric seeds pass through as themselves.
 *
 * Any decent string hash would do. What matters is that neighbouring seeds
 * ("level-1", "level-2") land far apart, which a naive character sum does not
 * manage — and neighbouring seeds are exactly what a level set uses.
 */
export function hashSeed(seed: string | number): number {
  if (typeof seed === 'number') return seed >>> 0;
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Mulberry32: 32 bits of state, one multiply-xorshift per value.
 *
 * Chosen for being tiny, dependency-free and easy to read. It is emphatically
 * not a cryptographic generator and does not need to be — the worst outcome of
 * a weak stream here is a town that looks slightly regular.
 */
function mulberry32(state: number): () => number {
  let a = state >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Build a stream from a seed. The same seed always gives the same stream. */
export function makeRng(seed: string | number): Rng {
  const rooted = hashSeed(seed);
  const next = mulberry32(rooted);

  const rng: Rng = {
    next,
    float: (min, max) => min + next() * (max - min),
    // `floor` over the half-open range, clamped so the 1-in-4-billion case
    // where next() rounds to 1 can't return max + 1.
    int: (min, max) => Math.min(max, min + Math.floor(next() * (max - min + 1))),
    chance: (p) => next() < p,
    pick: (items) => {
      if (items.length === 0) throw new Error('rng.pick: nothing to pick from');
      return items[Math.min(items.length - 1, Math.floor(next() * items.length))];
    },
    shuffle: (items) => {
      const out = [...items];
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    },
    // Hashed together with a separator rather than XORed. XOR is commutative,
    // so it gives `makeRng('bc').fork('a')` and `makeRng('a').fork('bc')` the
    // same stream — which would quietly tie two unrelated levels' stages
    // together. Re-hashing the pair does not.
    fork: (label) => makeRng(`${rooted}/${label}`),
  };
  return rng;
}
